import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import { Bell } from 'theme/icon';
import alertify from 'alertifyjs';
import * as actions from 'redux/actions/stockAlertActions';
import 'assets/css/alerts.css';

const OPERATORS = [
    { value: '>', label: '>' },
    { value: '<', label: '<' },
];

function resolvePrice(currentPrice, fallbackItem) {
    const fromProp = Number(currentPrice);
    if (Number.isFinite(fromProp) && fromProp > 0) {
        return fromProp;
    }
    const fromItem = Number(fallbackItem?.ask ?? fallbackItem?.bid);
    if (Number.isFinite(fromItem) && fromItem > 0) {
        return fromItem;
    }
    return 0.01;
}

const StockAlertModal = (props) => {
    const {
        symbol,
        currentPrice,
        item,
        buttonClassName = 'stock-watchlist-btn',
        iconSize = 18,
    } = props;

    const [open, setOpen] = useState(false);
    const [operator, setOperator] = useState('>');
    const [targetPrice, setTargetPrice] = useState(0.01);

    useEffect(() => {
        if (open) {
            setOperator('>');
            setTargetPrice(resolvePrice(currentPrice, item));
        }
    }, [open, currentPrice, item]);

    const handleClose = () => setOpen(false);

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            handleClose();
        }
    };

    const handleAdd = async () => {
        if (!symbol || !props.user_id) {
            return;
        }
        if (!operator) {
            alertify.error(I18n.t('alert_operator_required'));
            return;
        }

        const price = Number(targetPrice);
        if (!Number.isFinite(price) || price <= 0) {
            alertify.error(I18n.t('alert_price_required'));
            return;
        }

        const result = await props.newItem({
            id: props.user_id,
            symbol,
            operator,
            target_price: price,
        }, props.token);

        if (result?.success) {
            handleClose();
            alertify.success(I18n.t('alert_saved'));
            return;
        }

        const errorKey = result?.error && I18n.t(result.error) !== result.error
            ? result.error
            : 'alert_save_failed';
        alertify.error(I18n.t(errorKey));
    };

    if (!symbol) {
        return null;
    }

    return (
        <div className="stock-alert-modal-trigger">
            <button
                type="button"
                className={buttonClassName}
                onClick={() => setOpen(true)}
                title={I18n.t('alerts')}
                aria-label={I18n.t('alerts')}
            >
                <Bell size={iconSize} />
            </button>

            {open && (
                <div
                    className="dialog stock-alert-dialog"
                    role="presentation"
                    onClick={handleBackdropClick}
                >
                    <div
                        className="dialog-content watchlist-picker-content stock-alert-dialog-content"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="stock-alert-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 id="stock-alert-title" className="dialog-title">
                            <Translate value="alerts" />
                        </h2>
                        <p className="dialog-content-text">
                            <Translate value="watchlist_add_symbol" symbol={symbol.toUpperCase()} />
                        </p>

                        <div className="stock-alert-modal-fields">
                            <label className="stock-alert-field">
                                <span className="stock-alert-field-label">
                                    <Translate value="alerts_operator" />
                                </span>
                                <select
                                    className="input stock-alert-select"
                                    name="operator"
                                    value={operator}
                                    onChange={(e) => setOperator(e.target.value)}
                                >
                                    {OPERATORS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="stock-alert-field">
                                <span className="stock-alert-field-label">
                                    <Translate value="alerts_target_price" />
                                </span>
                                <input
                                    className="input stock-alert-price-input"
                                    name="target_price"
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={targetPrice}
                                    onChange={(e) => setTargetPrice(e.target.value)}
                                />
                            </label>
                        </div>

                        <div className="dialog-actions">
                            <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                <Translate value="close" />
                            </button>
                            <button type="button" className="btn btn-primary" onClick={handleAdd}>
                                <Translate value="alert_add" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
    };
}

const mapDispatchToProps = (dispatch) => ({
    newItem: (params, token) => dispatch(actions.newItem({ params, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(StockAlertModal);
